import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import StatusBadge from "../../../components/RestaurantAdminComponents/StatusBadge";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * RestaurantPastSettlementsPage — full past settlements table view.
 * Similar pattern to CustomersPage, showing all past settlements.
 * ---------------------------------------------------------------------- */

const columns = [
  {
    key: "restaurant",
    label: "Restaurant",
    width: "1.5fr",
  },
  {
    key: "period",
    label: "Period",
    width: "1.2fr",
    render: (r) => {
      const periodValue = r.period;
      if (typeof periodValue === 'object' && periodValue !== null) {
        return periodValue.label || periodValue.subtitle || `${periodValue.start_date} - ${periodValue.end_date}` || 'N/A';
      }
      return periodValue || 'N/A';
    }
  },
  {
    key: "totalMemberSpend",
    label: "Total Member Spend",
    width: "1fr",
    align: "right",
    render: (r) => r.totalMemberSpendDisplay || `$${r.totalMemberSpend?.toFixed(2) || '0.00'}`,
  },
  {
    key: "voucherRedemptionAmount",
    label: "Voucher Redemption",
    width: "1fr",
    align: "right",
    render: (r) => r.voucherRedemptionAmountDisplay || `$${r.voucherRedemptionAmount?.toFixed(2) || '0.00'}`,
  },
  {
    key: "restaurantNetRevenue",
    label: "Restaurant Net Revenue",
    width: "1fr",
    align: "right",
    render: (r) => r.restaurantNetRevenueDisplay || `$${r.restaurantNetRevenue?.toFixed(2) || '0.00'}`,
  },
  {
    key: "rewardFunding",
    label: "Reward Funding",
    width: "1fr",
    align: "right",
    render: (r) => r.rewardFundingDisplay || `$${r.rewardFunding?.toFixed(2) || '0.00'}`,
  },
  {
    key: "techFee",
    label: "Tech Fee",
    width: "1fr",
    align: "right",
    render: (r) => r.techFeeDisplay || `$${r.techFee?.toFixed(2) || '0.00'}`,
  },
  {
    key: "vouchersRedeemed",
    label: "Vouchers Redeemed",
    width: "1fr",
    align: "right",
    render: (r) => r.vouchersRedeemedDisplay || `$${r.vouchersRedeemed?.toFixed(2) || '0.00'}`,
  },
  {
    key: "totalOwed",
    label: "Total Owed",
    width: "1fr",
    align: "right",
    render: (r) => r.totalOwedDisplay || `$${r.totalOwed?.toFixed(2) || '0.00'}`,
  },
  {
    key: "netAmount",
    label: "Net Amount",
    width: "1fr",
    align: "right",
    render: (r) => r.netAmountDisplay || `$${r.netAmount?.toFixed(2) || '0.00'}`,
  },
  {
    key: "status",
    label: "Status",
    width: "1fr",
    render: (r) => <StatusBadge label={r.displayStatus || r.status || 'Unknown'} />,
  },
  {
    key: "transactionsCount",
    label: "Transactions",
    width: "0.8fr",
    align: "right",
    render: (r) => r.transactionsCount || 0,
  },
  {
    key: "platformFeesCount",
    label: "Platform Fees",
    width: "0.8fr",
    align: "right",
    render: (r) => r.platformFeesCount || 0,
  },
  {
    key: "vouchersRedeemedCount",
    label: "Vouchers Count",
    width: "0.8fr",
    align: "right",
    render: (r) => r.vouchersRedeemedCount || 0,
  },
  {
    key: "invoiceStatus",
    label: "Invoice",
    width: "1fr",
    render: (r) => r.invoiceStatus || (r.invoice ? 'Available' : 'N/A'),
  },
];

const RestaurantPastSettlementsPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Past Settlements | Omaha Food Lovers";
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("period_end");
  const [sortDirection, setSortDirection] = useState("desc");

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("page_size", 20);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const fetchSettlements = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.restaurantPastSettlements}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        const settlementsData = Array.isArray(data) ? data : (data.data || []);
        const newSettlements = settlementsData.map((settlement, index) => ({
          id: settlement.vendor_listing_id || settlement.id || `settlement-${index}`,
          restaurant: settlement.restaurant || 'Unknown',
          period: settlement.period || {
            start_date: settlement.period_start,
            end_date: settlement.period_end,
            label: settlement.period_label,
            subtitle: settlement.period_subtitle,
          },
          totalMemberSpend: settlement.total_member_spend?.amount || settlement.total_member_spend_cents / 100 || 0,
          totalMemberSpendDisplay: settlement.total_member_spend_display,
          voucherRedemptionAmount: settlement.voucher_redemption_amount?.amount || settlement.voucher_redemption_amount_cents / 100 || 0,
          voucherRedemptionAmountDisplay: settlement.voucher_redemption_amount_display,
          restaurantNetRevenue: settlement.restaurant_net_revenue?.amount || settlement.restaurant_net_revenue_cents / 100 || 0,
          restaurantNetRevenueDisplay: settlement.restaurant_net_revenue_display,
          rewardFunding: settlement.reward_funding || settlement.reward_funding_cents / 100 || 0,
          rewardFundingDisplay: settlement.reward_funding_display,
          techFee: settlement.tech_fee || settlement.tech_fee_cents / 100 || 0,
          techFeeDisplay: settlement.tech_fee_display,
          vouchersRedeemed: settlement.vouchers_redeemed || settlement.vouchers_redeemed_cents / 100 || 0,
          vouchersRedeemedDisplay: settlement.vouchers_redeemed_display,
          totalOwed: settlement.total_owed || settlement.total_owed_cents / 100 || 0,
          totalOwedDisplay: settlement.total_owed_display,
          netAmount: settlement.net_amount || settlement.net_amount_cents / 100 || 0,
          netAmountDisplay: settlement.net_amount_display,
          netDirection: settlement.net_direction || settlement.status || settlement.net_label || 'Unknown',
          status: settlement.status || settlement.net_direction || settlement.net_label || 'Unknown',
          displayStatus: settlement.display_status,
          transactionsCount: settlement.transactions_count || 0,
          platformFeesCount: settlement.platform_fees_count || 0,
          vouchersRedeemedCount: settlement.vouchers_redeemed_count || 0,
          invoice: settlement.invoice,
          invoiceId: settlement.invoice_id,
          invoiceStatus: settlement.invoice_status,
          statementAvailable: settlement.statement_available,
        }));

        if (isLoadMore) {
          setSettlements((prev) => [...prev, ...newSettlements]);
        } else {
          setSettlements(newSettlements);
        }

        // Simple pagination - if we got fewer results than page_size, no more data
        setHasMore(newSettlements.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch past settlements:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchSettlements(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.restaurantExport}?type=settlements`, {
        responseType: 'blob',
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'settlements_export.csv');
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toastr.success('Settlements exported successfully');
    } catch (error) {
      console.error('Failed to export settlements:', error);
      toastr.error('Failed to export settlements');
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "period_end",
      options: [
        { value: "period_start", label: "Period Start" },
        { value: "period_end", label: "Period End" },
        { value: "reward_funding", label: "Reward Funding" },
        { value: "vouchers", label: "Vouchers" },
        { value: "net_amount", label: "Net Amount" },
        { value: "created_at", label: "Created At" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchSettlements(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
           <h3 className="rp-explore-header__title">Past Settlements</h3>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by period"
            filters={filters}
            showExport
            onExport={handleExport}
          />

        <DataTable
          columns={columns}
          rows={settlements}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
        </div>
      </Container>
    </div>
  );
};

RestaurantPastSettlementsPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

RestaurantPastSettlementsPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default RestaurantPastSettlementsPage;
